import { useState } from 'react';
import {
  useListMyInvestmentsQuery,
  useListMyInvestmentPaymentsQuery,
  useListMyInvestmentPayoutsQuery,
} from '../../features/investments/investorInvestmentsApi';
import { usePlanLookup } from '../../components/investments/usePlanLookup';
import InvestmentCard from '../../components/investments/InvestmentCard';
import Card, { CardHeader } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import EmptyState from '../../components/ui/EmptyState';
import { TrendingUpIcon } from '../../components/ui/icons';
import { SkeletonCardGrid } from '../../components/ui/Skeleton';

// v1.17.0: "Pending payouts only" folded into this dropdown as its own
// option (client-only sentinel 'pending_payout', never sent to the server
// as a status) instead of a separate checkbox — mutually exclusive with a
// real status: selecting it sends has_pending_payout=1 with no status
// param, selecting a real status sends status=<value> with no
// has_pending_payout param.
const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'pending_funding', label: 'Pending funding' },
  { value: 'active', label: 'Active' },
  { value: 'matured', label: 'Matured' },
  { value: 'closed', label: 'Closed' },
  { value: 'pending_payout', label: 'Pending payout' },
];

/**
 * GET /investor/investments — read-only, scoped automatically to the
 * logged-in investor. Plan name is resolved via the already-integrated
 * catalog (usePlanLookup) since the investment response only carries
 * investment_plan_uuid, not plan details.
 */
export default function Investments() {
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const isPendingPayoutFilter = status === 'pending_payout';
  const { data, isLoading, isError } = useListMyInvestmentsQuery({
    status: isPendingPayoutFilter ? undefined : status || undefined,
    has_pending_payout: isPendingPayoutFilter,
    page,
  });
  const getPlan = usePlanLookup();

  const investments = data?.investments ?? [];
  const meta = data?.meta;
  const parseError = data?.parseError;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">My investments</h1>
          <p className="text-foreground-muted text-sm mt-1">Investments set up on your behalf by an administrator.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
            className="rounded-xl bg-background-elevated border border-border px-3.5 py-2.5 text-sm text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <Card>
        <CardHeader title="Investments" />

        {isLoading && <SkeletonCardGrid count={6} />}
        {isError && <p className="py-8 text-center text-danger">Could not load your investments. Check your connection and try again.</p>}
        {!isLoading && !isError && parseError && (
          <p className="py-8 text-center text-danger">Something went wrong loading this data. Check the console for details.</p>
        )}
        {!isLoading && !isError && !parseError && investments.length === 0 && (
          <EmptyState icon={TrendingUpIcon} title="No investments yet" body="An administrator sets these up on your behalf." />
        )}

        {!isLoading && !isError && !parseError && investments.length > 0 && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {investments.map((inv) => {
                const plan = getPlan(inv.investmentPlanUuid);
                return (
                  <InvestmentCard
                    key={inv.id}
                    investment={inv}
                    title={plan?.name || 'Investment'}
                    subtitle={plan ? `${plan.roi_percentage}% ROI` : undefined}
                    detailPath={`/my-investments/${inv.id}`}
                    usePaymentsQuery={useListMyInvestmentPaymentsQuery}
                    usePayoutsQuery={useListMyInvestmentPayoutsQuery}
                  />
                );
              })}
            </div>

            {meta && meta.lastPage > 1 && (
              <div className="flex items-center justify-between pt-4 mt-4 border-t border-border">
                <p className="text-xs text-foreground-subtle">
                  Page {meta.currentPage} of {meta.lastPage} · {meta.total} total
                </p>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" disabled={meta.currentPage <= 1} onClick={() => setPage(Math.max(1, meta.currentPage - 1))}>
                    Previous
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={meta.currentPage >= meta.lastPage}
                    onClick={() => setPage(Math.min(meta.lastPage, meta.currentPage + 1))}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
